import { sortBy } from 'lodash-es'
import { observer } from 'mobx-react'
import { useState } from 'react'

import DualListTabs from '@/component/molecules/DualList/components/DualListTabs'
import type {
  DualListItem,
  GroupByOption,
  StudentGroupedDualListProps,
} from '@/component/molecules/DualList/types'
import RealTimeSearchInput from '@/component/molecules/RealTimeSearchInput'
import type { Student, StudentGroupT } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { localStorageService } from '@/service/storage.service'
import stringUtils from '@/utils/String/stringUtils'

import {
  renderGroupedLeftEmptyView,
  renderGroupedRightEmptyView,
} from './components/GroupedEmptyView'
import { GroupedLeftContent } from './components/GroupedLeftContent'
import { GroupedRightContent } from './components/GroupedRightContent'
import { useGroupToggleState } from './hooks/useGroupToggleState'
import { createLessonClassGroupByOption, gradeGroupByOption } from './studentGroupByOptions'
import S from './StudentGroupedDualList.style'

type GroupByKey = StudentGroupT['type']

const getInitialGroupBy = (defaultGroupBy: GroupByKey): GroupByKey => {
  const stored = localStorageService.getItem('ASSIGN_TYPE')
  if (stored === 'grade' || stored === 'lesson-class') return stored
  return defaultGroupBy
}

/**
 * getGroups가 있으면 한 아이템을 여러 그룹에 배치, 없으면 getGroup으로 단일 그룹 배치.
 * groupKeyOrder가 있으면 해당 순서대로 키를 반환하여 렌더링 순서를 보장한다.
 */
const multiGroupBy = <T extends DualListItem>(
  items: T[],
  option: GroupByOption<T>,
): { groups: Record<string, T[]>; orderedKeys: string[] } => {
  const groups: Record<string, T[]> = {}

  for (const item of items) {
    const keys = option.getGroups?.(item) ?? [option.getGroup(item)]

    for (const key of keys) {
      if (key === undefined || key === null) continue

      const strKey = String(key)
      if (!groups[strKey]) groups[strKey] = []
      groups[strKey].push(item)
    }
  }

  const orderedKeys = option.groupKeyOrder
    ? option.groupKeyOrder.map(String).filter((k) => groups[k]?.length > 0)
    : Object.keys(groups).filter((k) => k !== 'undefined' && groups[k]?.length > 0)

  return { groups, orderedKeys }
}

const StudentGroupedDualList = ({
  dualListBox,
  searchPlaceholder = '이름 검색',
  disabledFilter,
  renderItemExtra,
  renderRightItem,
  renderRightContent,
  onBeforeMoveToRight,
  onBeforeMoveToLeft,
  rightHeaderTitle = '선택된 항목',
  renderRightHeaderDescription,
  showTabs = true,
  defaultOpen = false,
  defaultGroupBy = 'grade',
  studentLessonClassMap = new Map(),
  getLessonClassName = () => '',
  lessonClassOrder = [],
  lessonClassSearchPlaceholder = '학생이름 혹은 반이름 검색',
  leftListTestId,
  rightListTestId,
}: StudentGroupedDualListProps<Student>) => {
  const [searchValue, setSearchValue] = useState('')
  const [activeGroupBy, setActiveGroupBy] = useState<GroupByKey>(() =>
    showTabs ? getInitialGroupBy(defaultGroupBy ?? 'grade') : defaultGroupBy ?? 'grade',
  )
  const leftToggle = useGroupToggleState({ alwaysOpen: defaultOpen, searchValue })
  const rightToggle = useGroupToggleState({ status: 'open' })

  if (!dualListBox) return null

  const GROUP_BY_OPTIONS = [
    gradeGroupByOption,
    createLessonClassGroupByOption(studentLessonClassMap, getLessonClassName, lessonClassOrder),
  ]
  const activeOption =
    GROUP_BY_OPTIONS.find(({ key }) => key === activeGroupBy) ?? GROUP_BY_OPTIONS[0]

  const tabType = showTabs ? activeGroupBy : undefined

  const rightItems = dualListBox.rightList.toArr
  const filteredLeftItems = (() => {
    const nameFilteredItems = dualListBox.leftList.search('name', searchValue)
    const trimmed = searchValue.trim()

    const items = (() => {
      if (tabType !== 'lesson-class' || !trimmed) return nameFilteredItems

      return dualListBox.leftList.toArr.filter((item) => {
        if (stringUtils.matchesText(item.name, trimmed)) return true
        const classIds = studentLessonClassMap.get(item.id)
        if (!classIds || classIds.length === 0) return false
        return classIds.some((classId) =>
          stringUtils.matchesText(getLessonClassName(classId), trimmed),
        )
      })
    })()

    return sortBy(items, (item) => item.name)
  })()
  const isRightEmpty = rightItems.length === 0

  const { groups: leftGroups, orderedKeys: leftGroupKeys } = multiGroupBy(
    filteredLeftItems,
    activeOption,
  )
  const { groups: rightGroups, orderedKeys: rightGroupKeys } = multiGroupBy(
    rightItems,
    activeOption,
  )
  const teacherType = commonRepo.user?.type

  const rightHeaderDescription = renderRightHeaderDescription
    ? renderRightHeaderDescription(rightItems.length)
    : rightItems.length === 0
      ? '없음'
      : `${rightItems.length}명`
  const leftEmptyView = renderGroupedLeftEmptyView({
    isLeftEmpty: leftGroupKeys.length === 0,
    isRightEmpty,
    isSearchEmpty: searchValue !== '' && leftGroupKeys.length === 0,
    tabType,
    teacherType,
  })
  const rightEmptyView = renderGroupedRightEmptyView({
    isRightEmpty,
    isClassFilterEmpty: tabType === 'lesson-class' && !isRightEmpty && rightGroupKeys.length === 0,
  })

  const getGroupLabel = (groupKey: string | number) =>
    activeOption.getGroupLabel?.(groupKey) ?? String(groupKey)

  const handleMoveToRight = async (itemsToMove: Student[]) => {
    if (onBeforeMoveToRight) {
      const canMove = await onBeforeMoveToRight(itemsToMove)
      if (!canMove) return
    }
    dualListBox.moveLeftToRight(itemsToMove.map((item) => item.id))
  }

  const handleMoveToLeft = async (itemsToMove: Student[]) => {
    if (onBeforeMoveToLeft) {
      const canMove = await onBeforeMoveToLeft(itemsToMove)
      if (!canMove) return
    }
    dualListBox.moveRightToLeft(itemsToMove.map((item) => item.id))
  }

  const renderRight = () => {
    if (renderRightContent) {
      if (isRightEmpty) return rightEmptyView
      return renderRightContent(rightItems, handleMoveToLeft)
    }

    return (
      <GroupedRightContent
        groups={rightGroups}
        orderedGroupKeys={rightGroupKeys}
        isGroupOpen={rightToggle.isOpen}
        onToggleGroup={rightToggle.toggle}
        onMoveItems={handleMoveToLeft}
        getGroupLabel={getGroupLabel}
        disabledFilter={disabledFilter}
        renderItemExtra={renderItemExtra}
        renderItem={renderRightItem}
        emptyView={rightEmptyView}
      />
    )
  }

  return (
    <div css={S.container}>
      {showTabs && GROUP_BY_OPTIONS.length > 1 && (
        <nav css={S.nav}>
          <DualListTabs
            options={GROUP_BY_OPTIONS}
            activeKey={activeGroupBy}
            onChange={(nextTab: string) => {
              const groupByKey = nextTab as GroupByKey
              setActiveGroupBy(groupByKey)
              localStorageService.setItem('ASSIGN_TYPE')(groupByKey)
            }}
          />
        </nav>
      )}

      <main css={S.main}>
        <div css={S.leftListBox} className="list-box left-list-box" data-testid={leftListTestId}>
          <div css={S.searchWrap}>
            <RealTimeSearchInput
              placeholder={
                tabType === 'lesson-class' ? lessonClassSearchPlaceholder : searchPlaceholder
              }
              onSearch={setSearchValue}
              style={{ height: '42px' }}
            />
          </div>
          <div css={S.list} className="student-list">
            <GroupedLeftContent
              groups={leftGroups}
              orderedGroupKeys={leftGroupKeys}
              isGroupOpen={leftToggle.isOpen}
              onToggleGroup={leftToggle.toggle}
              onMoveItems={handleMoveToRight}
              getGroupLabel={getGroupLabel}
              disabledFilter={disabledFilter}
              renderItemExtra={renderItemExtra}
              emptyView={leftEmptyView}
            />
          </div>
        </div>
        <div css={S.rightListBox} className="list-box right-list-box" data-testid={rightListTestId}>
          <div css={S.rightHeader}>
            <strong>{rightHeaderTitle}</strong>
            <span>{rightHeaderDescription}</span>
          </div>
          <div css={S.list}>{renderRight()}</div>
        </div>
      </main>
    </div>
  )
}

export default observer(StudentGroupedDualList)
